import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  isSameMonth,
  isSameDay,
  addDays,
  isWithinInterval,
  parseISO,
  isBefore,
  startOfDay,
  differenceInCalendarDays,
} from 'date-fns';
import {
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  Dog,
} from 'lucide-react';
import { Customer, Booking } from '../types';
import { Avatar, Badge, Button, Card } from './ui';

interface CalendarViewProps {
  customers: Customer[];
  onEditBooking: (booking: Booking) => void;
}

const chipPalette = [
  { bg: 'bg-sage-200', text: 'text-sage-900' },
  { bg: 'bg-terracotta-200', text: 'text-terracotta-900' },
  { bg: 'bg-amber-200', text: 'text-amber-900' },
  { bg: 'bg-sky-200', text: 'text-sky-900' },
  { bg: 'bg-rose-200', text: 'text-rose-900' },
  { bg: 'bg-violet-200', text: 'text-violet-900' },
];

const CLIP_DEPARTURE = 'polygon(0 0, 100% 0, 0 100%)';
const CLIP_ARRIVAL = 'polygon(100% 0, 100% 100%, 0 100%)';
const CLIP_SINGLE = 'polygon(50% 0, 100% 50%, 50% 100%, 0 50%)';

interface BookingWithName extends Booking {
  customerName: string;
}

type RibbonVariant = 'arrival' | 'departure' | 'middle' | 'single';

// Find the most relevant booking — one in progress today, otherwise the next
// upcoming one. Returns undefined if no booking matches.
const findFocusBooking = <T extends Pick<Booking, 'fromDate' | 'toDate'>>(
  list: T[]
): T | undefined => {
  const now = new Date();
  const current = list.find((b) => {
    const from = parseISO(b.fromDate);
    const to = parseISO(b.toDate);
    return from <= now && now <= to;
  });
  if (current) return current;
  return list.find((b) => parseISO(b.fromDate) > now);
};

const CalendarView: React.FC<CalendarViewProps> = ({
  customers,
  onEditBooking,
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  // Track whether we've already auto-jumped to the focus month so we don't
  // override the user's manual nav once they've moved.
  const initializedRef = useRef(false);

  const allBookings = useMemo<BookingWithName[]>(() => {
    const list: BookingWithName[] = [];
    customers.forEach((customer) => {
      customer.bookings?.forEach((b) => {
        list.push({ ...b, customerName: customer.name });
      });
    });
    return list.sort(
      (a, b) => parseISO(a.fromDate).getTime() - parseISO(b.fromDate).getTime()
    );
  }, [customers]);

  // On first arrival of bookings, jump to the month of the current/next stay.
  useEffect(() => {
    if (initializedRef.current) return;
    if (allBookings.length === 0) return;
    const focus = findFocusBooking(allBookings);
    if (focus) setCurrentMonth(startOfMonth(parseISO(focus.fromDate)));
    initializedRef.current = true;
  }, [allBookings]);

  const colorByBooking = useMemo(() => {
    const map = new Map<string, (typeof chipPalette)[number]>();
    allBookings.forEach((b, i) => {
      map.set(b.id || b.customerId, chipPalette[i % chipPalette.length]);
    });
    return map;
  }, [allBookings]);

  const getColor = (b: BookingWithName) =>
    colorByBooking.get(b.id || b.customerId) || chipPalette[0];

  const lanes = useMemo<BookingWithName[][]>(() => {
    const result: BookingWithName[][] = [];
    for (const b of allBookings) {
      const bFrom = parseISO(b.fromDate);
      let placed = false;
      for (let i = 0; i < result.length; i++) {
        const last = result[i][result[i].length - 1];
        if (parseISO(last.toDate).getTime() <= bFrom.getTime()) {
          result[i].push(b);
          placed = true;
          break;
        }
      }
      if (!placed) result.push([b]);
    }
    return result;
  }, [allBookings]);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(monthStart);
  const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
  const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const days: Date[] = [];
  let day = startDate;
  while (day <= endDate) {
    days.push(day);
    day = addDays(day, 1);
  }

  const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const shouldLabel = (
    booking: BookingWithName,
    cellDay: Date,
    variant: RibbonVariant
  ): boolean => {
    const from = parseISO(booking.fromDate);
    const to = parseISO(booking.toDate);
    const stayDays = differenceInCalendarDays(to, from);
    const hasMiddle = stayDays > 1;

    if (variant === 'middle') return isSameDay(cellDay, addDays(from, 1));
    if (variant === 'single') return true;
    if (variant === 'arrival') return !hasMiddle;
    return false;
  };

  const renderLane = (cellDay: Date, laneIdx: number) => {
    const lane = lanes[laneIdx];
    const dayBookings = lane.filter((b) =>
      isWithinInterval(cellDay, {
        start: startOfDay(parseISO(b.fromDate)),
        end: startOfDay(parseISO(b.toDate)),
      })
    );

    if (dayBookings.length === 0)
      return <div key={laneIdx} className="h-5" />;

    if (dayBookings.length === 2) {
      const departing =
        dayBookings.find((b) => isSameDay(cellDay, parseISO(b.toDate))) ||
        dayBookings[0];
      const arriving =
        dayBookings.find((b) => isSameDay(cellDay, parseISO(b.fromDate))) ||
        dayBookings[1];
      return (
        <div key={laneIdx} className="relative h-5">
          <RibbonWedge
            booking={departing}
            variant="departure"
            color={getColor(departing)}
            showLabel={false}
            onClick={() => onEditBooking(departing)}
          />
          <RibbonWedge
            booking={arriving}
            variant="arrival"
            color={getColor(arriving)}
            showLabel={shouldLabel(arriving, cellDay, 'arrival')}
            onClick={() => onEditBooking(arriving)}
          />
        </div>
      );
    }

    const booking = dayBookings[0];
    const isStart = isSameDay(cellDay, parseISO(booking.fromDate));
    const isEnd = isSameDay(cellDay, parseISO(booking.toDate));
    const variant: RibbonVariant =
      isStart && isEnd
        ? 'single'
        : isStart
        ? 'arrival'
        : isEnd
        ? 'departure'
        : 'middle';

    return (
      <div key={laneIdx} className="relative h-5">
        <RibbonWedge
          booking={booking}
          variant={variant}
          color={getColor(booking)}
          showLabel={shouldLabel(booking, cellDay, variant)}
          onClick={() => onEditBooking(booking)}
        />
      </div>
    );
  };

  // Bookings within or overlapping the current month — for the agenda view.
  const monthBookings = useMemo(() => {
    return allBookings.filter((b) => {
      const from = parseISO(b.fromDate);
      const to = parseISO(b.toDate);
      return from <= monthEnd && to >= monthStart;
    });
  }, [allBookings, monthStart, monthEnd]);

  const focusId = useMemo(() => {
    const focus = findFocusBooking(monthBookings);
    return focus?.id;
  }, [monthBookings]);

  // Scroll the focus row into view on mobile after first render of a month.
  const agendaContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!focusId || !agendaContainerRef.current) return;
    const el = agendaContainerRef.current.querySelector<HTMLElement>(
      `[data-booking-id="${focusId}"]`
    );
    if (el) el.scrollIntoView({ behavior: 'auto', block: 'start' });
  }, [focusId, currentMonth]);

  const fmtAgendaDate = (s: string) => format(parseISO(s), 'EEE d MMM');

  const monthHeader = (
    <div className="flex items-center justify-between gap-2 px-3 md:px-5 py-3 md:py-4 border-b border-sand-200 flex-wrap">
      <div className="flex items-center gap-2 md:gap-3 min-w-0">
        <h2 className="text-base md:text-lg font-semibold text-sand-900 font-display truncate">
          {format(currentMonth, 'MMMM yyyy')}
        </h2>
        <div className="flex items-center gap-1 ml-1 md:ml-2 shrink-0">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            aria-label="Previous month"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setCurrentMonth(new Date())}
          >
            Today
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            aria-label="Next month"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <Legend />
    </div>
  );

  return (
    <div className="p-3 md:p-6">
      <Card className="overflow-hidden">
        {monthHeader}

        {/* Desktop / tablet: full grid calendar */}
        <div className="hidden md:block">
          <div className="grid grid-cols-7 border-b border-sand-200 bg-sand-50/60">
            {dayLabels.map((d) => (
              <div
                key={d}
                className="py-2.5 text-center text-[11px] font-medium text-sand-500 uppercase tracking-wider"
              >
                {d}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 bg-white">
            {days.map((d) => {
              const isCurrent = isSameMonth(d, monthStart);
              const isToday = isSameDay(d, new Date());
              const isPast = isBefore(startOfDay(d), startOfDay(new Date()));

              return (
                <div
                  key={d.toString()}
                  className={[
                    'min-h-[110px] border-r border-b border-sand-100 p-1.5 relative',
                    !isCurrent ? 'bg-sand-50/40' : '',
                    isToday ? 'bg-sage-50/40' : '',
                  ].join(' ')}
                >
                  <div className="flex items-center justify-between mb-1.5 px-1">
                    <span
                      className={[
                        'text-xs font-medium',
                        isToday
                          ? 'inline-flex items-center justify-center w-6 h-6 bg-sage-600 text-white rounded-full'
                          : isCurrent
                          ? isPast
                            ? 'text-sand-400'
                            : 'text-sand-800'
                          : 'text-sand-300',
                      ].join(' ')}
                    >
                      {format(d, 'd')}
                    </span>
                  </div>

                  <div className="space-y-1">
                    {lanes.map((_lane, laneIdx) => renderLane(d, laneIdx))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile: agenda list */}
        <div ref={agendaContainerRef} className="md:hidden divide-y divide-sand-100">
          {monthBookings.length === 0 && (
            <div className="py-12 text-center">
              <p className="text-sm text-sand-700 font-medium">
                No bookings this month
              </p>
              <p className="text-xs text-sand-500 mt-1">
                Use the navigation arrows to browse other months.
              </p>
            </div>
          )}

          {monthBookings.map((b) => {
            const c = getColor(b);
            const nights = differenceInCalendarDays(
              parseISO(b.toDate),
              parseISO(b.fromDate)
            );
            const isFocus = focusId === b.id;
            return (
              <button
                key={b.id || b.customerId}
                data-booking-id={b.id}
                onClick={() => onEditBooking(b)}
                className={[
                  'w-full text-left px-4 py-3 transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500/30',
                  isFocus ? 'bg-sage-50/60 hover:bg-sage-50' : 'hover:bg-sand-50',
                ].join(' ')}
              >
                <div className="flex items-start gap-3">
                  <div className="shrink-0 mt-0.5">
                    <Avatar name={b.customerName} size="sm" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <span className="text-sm font-medium text-sand-900 truncate">
                        {b.customerName}
                        {isFocus && (
                          <span className="ml-2 text-[10px] font-medium text-sage-700 bg-sage-100 px-1.5 py-0.5 rounded">
                            Now
                          </span>
                        )}
                      </span>
                      <span
                        className={[
                          'text-[10px] font-medium px-2 py-0.5 rounded-full',
                          c.bg,
                          c.text,
                        ].join(' ')}
                      >
                        {nights} {nights === 1 ? 'night' : 'nights'}
                      </span>
                    </div>
                    <p className="text-xs text-sand-700 flex items-center gap-1.5">
                      {fmtAgendaDate(b.fromDate)}
                      <ArrowRight className="h-3 w-3 text-sand-400" />
                      {fmtAgendaDate(b.toDate)}
                    </p>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                      <Badge tone="sand">
                        {b.adults}A · {b.children}C · {b.infants}I
                      </Badge>
                      {b.dogs > 0 && (
                        <Badge tone="sage">
                          <Dog className="h-3 w-3 mr-0.5 inline" />
                          {b.dogs}
                        </Badge>
                      )}
                      <span className="text-xs text-sage-700 ml-auto font-medium">
                        £{Number(b.ourIncome || 0).toLocaleString('en-GB')}
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </Card>
    </div>
  );
};

interface RibbonWedgeProps {
  booking: BookingWithName;
  variant: RibbonVariant;
  color: { bg: string; text: string };
  showLabel: boolean;
  onClick: () => void;
}

const RibbonWedge: React.FC<RibbonWedgeProps> = ({
  booking,
  variant,
  color,
  showLabel,
  onClick,
}) => {
  let clipPath: string | undefined;
  if (variant === 'arrival') clipPath = CLIP_ARRIVAL;
  else if (variant === 'departure') clipPath = CLIP_DEPARTURE;
  else if (variant === 'single') clipPath = CLIP_SINGLE;

  const title =
    booking.customerName +
    ' · ' +
    format(parseISO(booking.fromDate), 'd MMM') +
    ' – ' +
    format(parseISO(booking.toDate), 'd MMM');

  const justify =
    variant === 'middle'
      ? 'justify-start'
      : variant === 'arrival'
      ? 'justify-end'
      : 'justify-center';

  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      style={{ clipPath }}
      className={[
        'absolute inset-0 flex items-center text-left px-1.5 transition-opacity hover:opacity-85',
        color.bg,
        color.text,
        justify,
      ].join(' ')}
    >
      {showLabel && (
        <span className="text-[10px] font-medium truncate max-w-full">
          {booking.customerName}
        </span>
      )}
    </button>
  );
};

const Legend: React.FC = () => (
  <div className="flex items-center gap-3 text-xs text-sand-500">
    <span className="hidden sm:inline-flex items-center gap-2">
      <span
        className="w-4 h-4 inline-block bg-sage-200"
        style={{ clipPath: CLIP_DEPARTURE }}
      />
      Out
    </span>
    <span className="hidden sm:inline-flex items-center gap-2">
      <span
        className="w-4 h-4 inline-block bg-terracotta-200"
        style={{ clipPath: CLIP_ARRIVAL }}
      />
      In
    </span>
    <span className="hidden sm:inline-flex items-center gap-2">
      <span className="w-4 h-2 inline-block bg-sand-200" />
      Stay
    </span>
  </div>
);

export default CalendarView;
